import { useState } from "react";

export type SortOrder = "asc" | "desc";
export type SortBy = "date" | "type";

export interface SortParams {
	sort_by?: SortBy;
	sort_order?: SortOrder;
}

interface UseSortOptions {
	initialSortBy: SortBy;
	initialSortOrder: SortOrder;
	onSortChange?: (sortBy: SortBy, sortOrder: SortOrder) => void;
}

function useSort(options: UseSortOptions) {
	const {
		initialSortBy = "date",
		initialSortOrder = "desc",
		onSortChange,
	} = options;

	const [sortBy, setSortBy] = useState<SortBy>(initialSortBy);
	const [sortOrder, setSortOrder] = useState<SortOrder>(initialSortOrder);

	function handleSortFieldChange(field: SortBy) {
		setSortBy((prevSortBy) => {
			if (field === prevSortBy) {
				setSortOrder((prevSortOrder) => {
					const toggled = prevSortOrder === "asc" ? "desc" : "asc";
					onSortChange?.(field, toggled);
					return toggled;
				});
				return prevSortBy;
			}

			setSortOrder("asc");
			onSortChange?.(field, "asc");
			return field;
		});
	}

	function handleSortOrderChange(newSortOrder: SortOrder) {
		setSortOrder(() => {
			onSortChange?.(sortBy, newSortOrder);
			return newSortOrder;
		});
	}

	function resetSort() {
		setSortBy(initialSortBy);
		setSortOrder(initialSortOrder);
		if (initialSortBy) {
			onSortChange?.(initialSortBy, initialSortOrder);
		}
	}

	return {
		sortBy,
		sortOrder,
		handleSortFieldChange,
		handleSortOrderChange,
		resetSort,
		getSortParams: (): SortParams => ({
			sort_by: sortBy || undefined,
			sort_order: sortBy ? sortOrder : undefined,
		}),
	};
}

export { useSort };
